import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getToken, URL } from "../../utils";
import { toast, ToastContainer } from "react-toastify";
import Dropzone from "../../components/Dropzone";
import CountryField from "../../components/CountryField";
import { useCountries } from "../../hooks/useCountries";
import { getCountryCode } from "../../utils/countryUtils";


// Construit un message lisible à partir des erreurs renvoyées par l'API (DRF).
const formatApiErrors = (data) => {
  if (!data) return "Une erreur est survenue.";
  if (typeof data === "string") return data;
  if (data.detail) return data.detail;
  try {
    const msgs = Object.entries(data).map(([field, val]) => {
      const text = Array.isArray(val) ? val.join(", ") : String(val);
      return `${field} : ${text}`;
    });
    return msgs.length ? msgs.join(" | ") : "Erreur de validation.";
  } catch {
    return "Erreur de validation. Veuillez vérifier les champs.";
  }
};

export default function ProfessionalsEditForm() {
  const [nom, setNom] = useState("");
  const [secteur, setSecteur] = useState("");
  const [adresse, setAdresse] = useState("");
  const [contact, setContact] = useState("");
  const [active, setActive] = useState(false);
  const [registre, setRegistre] = useState("");
  const [country, setCountry] = useState("");
  const [originalCountry, setOriginalCountry] = useState("");
  const [carteCFE, setCarteCFE] = useState(null);
  const [carteCFEUrl, setCarteCFEUrl] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const navigate = useNavigate();
  const { id } = useParams();
  const token = getToken();
  const { countries } = useCountries();

  // Définition de fetchData avec useCallback
  const fetchData = useCallback(async () => {
    try {
      const [response] = await Promise.all([
        axios.get(URL + "Clients/detail-client-professionnel/" + id, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      const pro = response.data;

      setNom(pro.nom_entreprise);
      setSecteur(pro.secteur_activite);
      setAdresse(pro.adresse);
      setRegistre(pro.registre_commerce);
      setContact(pro.contact);
      setActive(pro.is_active);
      // L'API renvoie le nom du pays dans "pays" ; on stocke la valeur brute,
      // la conversion en code ISO se fait une fois la liste des pays chargée.
      const rawPays = pro.pays ?? pro.country ?? "";
      setCountry(rawPays);
      setOriginalCountry(rawPays);

      // Si l'URL de la carte CFE existe, on la définit
      if (pro.carte_cfe) {
        setCarteCFEUrl(pro.carte_cfe);
      }
    } catch (error) {
      toast.error("Erreur lors de la récupération des données");
    }
  }, [id, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Convertit le nom du pays renvoyé par l'API en code ISO une fois la liste chargée.
  useEffect(() => {
    if (originalCountry && countries.length > 1) {
      setCountry(getCountryCode(originalCountry, countries));
    }
  }, [originalCountry, countries]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});

    // Création d'un FormData pour gérer l'upload de fichier
    const formData = new FormData();
    formData.append("nom_entreprise", nom);
    formData.append("secteur_activite", secteur);
    formData.append("registre_commerce", registre);
    formData.append("adresse", adresse);
    formData.append("contact", contact);
    formData.append("pays", getCountryCode(country, countries));
    formData.append("is_active", active);

    // Ajout de l'image de la carte CFE si elle existe
    if (carteCFE) {
      formData.append("carte_cfe", carteCFE);
    }

    try {
      await axios.put(
        URL + "Clients/edit-client-professionnel/" + id,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      navigate("/clients/professionnels/show/" + id);
      toast.success("Modifications enregistrées avec succès !");
    } catch (error) {
      if (error.response && error.response.data) {
        setValidationErrors(error.response.data);
        toast.error(formatApiErrors(error.response.data));
      } else if (error.response) {
        toast.error("Erreur lors de l'enregistrement des modifications.");
      } else {
        toast.error(
          error.message || "Erreur réseau. Vérifiez votre connexion."
        );
      }
    }
  };

  // Gestionnaire pour l'upload de l'image de la carte CFE
  const handleCarteCFEUpload = useCallback((file) => {
    if (file) {
      // Vérifier et traiter le nom du fichier si nécessaire
      if (file.name.length > 100) {
        // Extraire l'extension
        const extension = file.name.split(".").pop();

        // Conserver une partie du nom original pour garder une référence
        const originalNamePart = file.name.substring(0, 30); // Prendre les 30 premiers caractères

        // Créer un nouveau nom de fichier plus court
        const timestamp = Date.now().toString().slice(-6); // Derniers 6 chiffres du timestamp
        const newFileName = `${originalNamePart}...${timestamp}.${extension}`;

        // Créer un nouveau fichier avec le nom raccourci
        const newFile = new File([file], newFileName, { type: file.type });
        setCarteCFE(newFile);

        // Informer l'utilisateur
        toast.info(
          `Nom de fichier raccourci: "${file.name}" → "${newFileName}"`
        );

        // Prévisualisation du fichier
        const reader = new FileReader();
        reader.onloadend = () => {
          setCarteCFEUrl(reader.result);
        };
        reader.readAsDataURL(newFile);
      } else {
        setCarteCFE(file);

        // Prévisualisation du fichier
        const reader = new FileReader();
        reader.onloadend = () => {
          setCarteCFEUrl(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  }, []);

  return (
    <div className="p-4 w-[min(600px,90%)] mx-auto mt-4 bg-white shadow-md rounded-md">
      <ToastContainer />
      <div className="flex gap-4 p-4 pb-0">
        <div className="p-3 bg-amber-200 rounded-full text-amber-600 grid justify-center items-center">
          <svg
            className="w-8 h-8"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M16 12h4m-2 2v-4M4 18v-1a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Zm8-10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
          </svg>
        </div>
        <div className="">
          <h1 className="text-lg font-semibold">Modifier le professionnel</h1>
          <p className="text-sm text-gray-500">
            Modifiez les informations du professionnel
          </p>
        </div>
      </div>
      <form className="p-4" onSubmit={handleSubmit}>
        <div className="mb-4">
          <label
            htmlFor="nom"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Nom de l'entreprise
          </label>
          <input
            type="text"
            id="nom"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            placeholder="Nom de l'entreprise"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            required
          />
          {validationErrors.nom_entreprise && (
            <p className="text-red-500 text-xs">
              {validationErrors.nom_entreprise}
            </p>
          )}
        </div>
        <div className="mb-4">
          <label
            htmlFor="registre"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Numero de registre de commerce
          </label>
          <input
            type="text"
            id="registre"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            placeholder="Numero de registre de commerce"
            value={registre}
            onChange={(e) => setRegistre(e.target.value)}
            required
          />
          {validationErrors.registre_commerce && (
            <p className="text-red-500 text-xs">
              {validationErrors.registre_commerce}
            </p>
          )}
        </div>

        {/* Country */}
        <div className="mb-4">
          <CountryField
            label="Pays"
            value={country}
            onChange={(selectedOption) =>
              setCountry(selectedOption ? selectedOption.value : "")
            }
            countries={countries}
            error={validationErrors.pays}
            required={true}
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="secteur"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Secteur d'activité
          </label>
          <select
            id="secteur"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={secteur}
            onChange={(e) => setSecteur(e.target.value)}
            required
          >
            <option value="">--- Choisir un secteur ---</option>
            <option value="shop">Boutique</option>
            <option value="bar">Bar</option>
            <option value="restaurant">Restaurant</option>
            <option value="hôtel">Hôtel</option>
            <option value="Company">Compagnie</option>
            <option value="night_etablissement">Établissement de nuit</option>
          </select>
          {validationErrors.secteur_activite && (
            <p className="text-red-500 text-xs">
              {validationErrors.secteur_activite}
            </p>
          )}
        </div>
        <div className="mb-4 flex gap-4">
          <div className="flex-grow">
            <label
              htmlFor="adresse"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Adresse
            </label>
            <input
              type="text"
              id="adresse"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              placeholder="Adresse"
              value={adresse}
              onChange={(e) => setAdresse(e.target.value)}
              required
            />
            {validationErrors.adresse && (
              <p className="text-red-500 text-xs">{validationErrors.adresse}</p>
            )}
          </div>
          <div className="flex-grow">
            <label
              htmlFor="contact"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Contact
            </label>
            <input
              type="tel"
              id="contact"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              placeholder="Contact"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              required
            />
            {validationErrors.contact && (
              <p className="text-red-500 text-xs">{validationErrors.contact}</p>
            )}
          </div>
        </div>
        <div className="mb-4">
          <label
            htmlFor="carteCFE"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Carte CFE
          </label>
          <Dropzone onFileUpload={handleCarteCFEUpload} />
          {carteCFEUrl && (
            <div className="mt-2">
              <a
                href={carteCFEUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline"
              >
                Voir la carte CFE
              </a>
            </div>
          )}
          {validationErrors.carte_cfe && (
            <p className="text-red-500 text-xs">{validationErrors.carte_cfe}</p>
          )}
        </div>
        <div className="flex items-center gap-4 mb-4">
          <label htmlFor="active" className="flex items-center">
            <input
              type="checkbox"
              id="active"
              className="form-checkbox text-amber-600 rounded-md h-6 w-6"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            <span className="ml-2 text-sm text-gray-900">Actif</span>
          </label>
        </div>
        <div className="grid grid-cols-2 items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="hover:text-white border border-gray-800 bg-gray-200 hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="text-white bg-amber-600 hover:bg-amber-700 focus:ring-4 focus:outline-none focus:ring-amber-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center"
          >
            Modifier
          </button>
        </div>
      </form>
    </div>
  );
}
